import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Star, StarOff, 
  Save, X, ArrowLeft, Image, Video, Search, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'news', label: 'News' },
  { id: 'tips', label: 'Pet Care Tips' },
  { id: 'health', label: 'Health & Wellness' },
  { id: 'training', label: 'Training' },
  { id: 'community', label: 'Community Stories' },
  { id: 'video', label: 'Videos' },
  { id: 'announcement', label: 'Announcements' }
];

const BlogAdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'news',
    tags: '',
    image_url: '',
    video_url: '',
    is_video: false,
    is_featured: false,
    status: 'draft'
  });

  useEffect(() => {
    if (!user?.is_admin && !user?.is_sysadmin && !user?.is_operator_admin) {
      navigate('/');
      return;
    }
    fetchPosts();
  }, [user, navigate]);

  // Auto-open editor if ?edit=postId is in URL
  useEffect(() => {
    const editPostId = searchParams.get('edit');
    if (editPostId && posts.length > 0) {
      const postToEdit = posts.find(p => p.id === editPostId);
      if (postToEdit) {
        openEditor(postToEdit);
      } else {
        // Post not in current list, try to fetch it directly
        fetchAndEditPost(editPostId);
      }
    }
  }, [searchParams, posts]);

  const fetchAndEditPost = async (postId) => {
    try {
      const response = await fetch(`${API}/api/blog/posts/${postId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const post = await response.json();
        openEditor(post);
      }
    } catch (error) {
      console.error('Error fetching post for editing:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/blog/admin/posts?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }));
  };

  const openEditor = async (post = null) => {
    if (post) {
      // Fetch full post content for editing
      try {
        const response = await fetch(`${API}/api/blog/posts/${post.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const fullPost = await response.json();
          setEditingPost(fullPost);
          setFormData({
            title: fullPost.title,
            slug: fullPost.slug,
            excerpt: fullPost.excerpt || '',
            content: fullPost.content || '',
            category: fullPost.category,
            tags: (fullPost.tags || []).join(', '),
            image_url: fullPost.image_url || '',
            video_url: fullPost.video_url || '',
            is_video: fullPost.is_video || false,
            is_featured: fullPost.is_featured || false,
            status: fullPost.status || 'draft'
          });
        } else {
          // Fallback to list data
          setEditingPost(post);
          setFormData({
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt || '',
            content: post.content || '',
            category: post.category,
            tags: (post.tags || []).join(', '),
            image_url: post.image_url || '',
            video_url: post.video_url || '',
            is_video: post.is_video || false,
            is_featured: post.is_featured || false,
            status: post.status || 'draft'
          });
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        // Fallback to list data
        setEditingPost(post);
        setFormData({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt || '',
          content: post.content || '',
          category: post.category,
          tags: (post.tags || []).join(', '),
          image_url: post.image_url || '',
          video_url: post.video_url || '',
          is_video: post.is_video || false,
          is_featured: post.is_featured || false,
          status: post.status || 'draft'
        });
      }
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'news',
        tags: '',
        image_url: '',
        video_url: '',
        is_video: false,
        is_featured: false,
        status: 'draft'
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingPost(null);
  };

  const handleSave = async (publishNow = false) => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        status: publishNow ? 'published' : formData.status
      };

      const url = editingPost 
        ? `${API}/api/blog/posts/${editingPost.id}`
        : `${API}/api/blog/posts`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchPosts();
        closeEditor();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to save post'}`);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API}/api/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPosts(posts.filter(p => p.id !== postId));
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const toggleFeatured = async (post) => {
    try {
      const response = await fetch(`${API}/api/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_featured: !post.is_featured })
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const togglePublished = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(`${API}/api/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/blog')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Blog Admin</h1>
            </div>
            <button
              onClick={() => openEditor()}
              data-testid="new-post-button"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5" />
              New Post
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-posts-input"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter-select"
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Posts Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 mb-4">No blog posts found</p>
            <button
              onClick={() => openEditor()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first post
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Title</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden lg:table-cell">Stats</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Featured</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredPosts.map(post => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => openEditor(post)}
                        data-testid={`edit-post-${post.id}`}
                      >
                        {post.image_url && (
                          <img 
                            src={post.image_url} 
                            alt="" 
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900 line-clamp-1 hover:text-blue-600">{post.title}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full capitalize">
                        {post.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="text-sm text-gray-600">
                        <span>{post.views_count || 0} views</span>
                        <span className="mx-2">·</span>
                        <span>{post.likes_count || 0} likes</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => togglePublished(post)}
                        className={`p-1.5 rounded-lg transition ${
                          post.status === 'published'
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        {post.status === 'published' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleFeatured(post)}
                        className={`p-1.5 rounded-lg transition ${
                          post.is_featured
                            ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        title={post.is_featured ? 'Remove from featured' : 'Set as featured'}
                      >
                        {post.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditor(post)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-auto">
            {/* Editor Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </h2>
              <button onClick={closeEditor} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Editor Form */}
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Title & Slug */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Enter post title"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="url-friendly-slug"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Category & Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    placeholder="puppy, training, tips"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Excerpt/Summary</label>
                  <span className={`text-xs ${
                    (formData.excerpt?.length || 0) > 160 
                      ? 'text-red-500' 
                      : (formData.excerpt?.length || 0) > 120 
                        ? 'text-amber-500' 
                        : 'text-gray-400'
                  }`}>
                    {formData.excerpt?.length || 0}/160 chars
                    {(formData.excerpt?.length || 0) > 160 && ' (too long for SEO)'}
                  </span>
                </div>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder="Brief summary of the post (shown in listings and SEO). Aim for 120-160 characters."
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      (formData.excerpt?.length || 0) > 160 ? 'bg-red-500' :
                      (formData.excerpt?.length || 0) > 120 ? 'bg-green-500' :
                      (formData.excerpt?.length || 0) > 60 ? 'bg-amber-400' : 'bg-gray-300'
                    }`}
                    style={{ width: `${Math.min(100, ((formData.excerpt?.length || 0) / 160) * 100)}%` }}
                  />
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content * (Markdown supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Write your post content here... Markdown is supported!"
                  rows={12}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  required
                />
              </div>

              {/* Media */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Image className="w-4 h-4 inline mr-1" />
                    Image
                  </label>
                  <div className="space-y-2">
                    {/* File Upload */}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Convert to base64 for preview, or upload to storage
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setFormData({...formData, image_url: reader.result});
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                        id="image-upload"
                      />
                      <label 
                        htmlFor="image-upload"
                        className="flex-1 px-3 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-gray-50 text-center text-sm text-gray-500"
                      >
                        Click to upload image
                      </label>
                    </div>
                    {/* URL Input */}
                    <input
                      type="url"
                      value={formData.image_url?.startsWith('data:') ? '' : formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="Or paste image URL..."
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    {/* Preview */}
                    {formData.image_url && (
                      <div className="relative">
                        <img 
                          src={formData.image_url} 
                          alt="Preview" 
                          className="w-full h-24 object-cover rounded-lg"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <button
                          onClick={() => setFormData({...formData, image_url: ''})}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Video className="w-4 h-4 inline mr-1" />
                    Video URL
                  </label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({...formData, video_url: e.target.value, is_video: !!e.target.value})}
                    placeholder="https://youtube.com/..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Options - Stack on mobile */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Featured Post</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_video}
                    onChange={(e) => setFormData({...formData, is_video: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Video Post</span>
                </label>
              </div>
            </div>

            {/* Editor Footer - Mobile Responsive */}
            <div className="px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              {/* Mobile: Stack vertically */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {/* Status selector */}
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="px-3 py-2 border rounded-lg text-sm w-full sm:w-auto"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
                
                {/* Action buttons - wrap on mobile */}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    onClick={closeEditor}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSave(false)}
                    disabled={saving || !formData.title || !formData.content}
                    className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50 text-sm"
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden xs:inline">Save</span>
                  </button>
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving || !formData.title || !formData.content}
                    className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    <span>Publish</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdminDashboard;
